import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';

import i18n from 'i18next';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { loadProjectFolders } from 'scenes/ProjectBank/store/thunks/loadProjectFolders.thunk';
import {
  setFolderTree,
  setInitialProjectBankPath,
  setSelectedProjectBankFolder,
} from 'store/filepermissions/actions/filepermissions.actions';
import {
  clearSelectedPrivateProjectBankItems,
  clearSelectedProjectBankFiles,
  closePdfPreviewDialog,
  closePrivateProjectBankTreeFolders,
  closeProjectBank,
  closeProjectTreeFolders,
  closeTransferFolderStructureDialog,
  openPdfPreviewDialog,
  openPrivateProjectBankTreeFolders,
  openProjectTilaajavastuuFolder,
  openProjectTreeFolders,
  openTransferFolderStructureDialog,
  selectPrivateProjectBankItem,
  setIsAddFolderDialogOpen,
  setIsProjectBankFileSelectDialogOpen,
  setProjectBankViewMode,
  setSelectedPrivateProjectBankFolderId,
  setSelectedProjectBankDataTransferTargetVisibilityMode,
  setSelectedProjectBankVisibilityMode,
  setSelectedTilaajavastuuBusinessId,
  setSortBy,
  setUpdateItemsInState,
  toggleSelectProjectBankFile,
} from '../actions/project-bank.actions';
import { createProjectBankFile } from '../thunks/createProjectBankFile.thunk';
import { createProjectBankFolder } from '../thunks/createProjectBankFolder.thunk';
import { deleteTilaajavastuuDocument } from '../thunks/deleteTilaajavastuuDocument.thunk';
import { duplicateItems } from '../thunks/duplicateItems.thunk';
import { loadBusinessTilaajavastuuIntegrationStatus } from '../thunks/loadBusinessTilaajavastuuIntegrationStatus.thunk';
import { loadProjectAndSubProjectTilaajavastuuDocuments } from '../thunks/loadProjectAndSubProjectTilaajavastuuDocuments.thunk copy';
import { loadProjectBankFolderContents } from '../thunks/loadProjectBankFolderContents.thunk';
import { loadProjectTilaajavastuuDocuments } from '../thunks/loadProjectTilaajavastuuDocuments.thunk';
import { createPrivateProjectBankFolder } from '../thunks/private-project-bank/createPrivateProjectBankFolder.thunk';
import { deletePrivateProjectBankItem } from '../thunks/private-project-bank/deletePrivateProjectBankFile.thunk';
import { loadPrivateProjectBankItemsOfProject } from '../thunks/private-project-bank/loadPrivateProjectBankItemsOfProject.thunk';
import { renamePrivateProjectBankItem } from '../thunks/private-project-bank/renamePrivateProjectBankItem.thunk';
import { uploadPrivateProjectBankFile } from '../thunks/private-project-bank/uploadPrivateProjectBankFile.thunk';
import { setAsDeletedProjectBankFilePermission } from '../thunks/setAsDeletedProjectBankFilePermission.thunk';
import { transferFolderStructureToProject } from '../thunks/transferFolderStructureToProject.thunk';
import { transferItems } from '../thunks/transferItems.thunk';
import { transferItemsBetweenPrivateAndPublicProjectBank } from '../thunks/transferItemsBetweenPrivateAndPublicProjectBank.thunk';
import { updateProjectBankItem } from '../thunks/updateProjectBankItem.thunk';

const filesFilePermissionsAdapter = createEntityAdapter();
const projectFolderFilePermissionsAdapter = createEntityAdapter();
const tilaajavastuuDocumentsAdapter = createEntityAdapter({
  selectId: (tilaajavastuuDocument) =>
    `${tilaajavastuuDocument.projectId}#${tilaajavastuuDocument.businessId}`,
});

const privateProjectBankItemsAdapter = createEntityAdapter();

const initialState = {
  projectTreeLoading: false,
  isFolderFilesLoading: false,
  filesFilePermissions: filesFilePermissionsAdapter.getInitialState(),
  projectFolderFilePermissions:
    projectFolderFilePermissionsAdapter.getInitialState(),
  tilaajavastuuDocuments: tilaajavastuuDocumentsAdapter.getInitialState(),
  tilaajavastuuIntegrationStatusesByBusinessId: {},
  selectedTilaajavastuuBusinessId: '',
  selectedFolderPath: '',
  selectedFolderId: '',
  initialProjectBankPath: '',
  folderTree: {},
  sortBy: '',
  isLoadingTilaajavastuuDocuments: false,
  selectedTilaajavastuuFolderProjectId: '',
  openedProjectTreeFolderIds: [],
  isPdfPreviewDialogOpen: false,
  isProjectBankFileSelectDialogOpen: false,
  isTransferFolderStructureDialogOpen: false,
  isLoadingFolderStructureTransfer: false,
  transferFolderStructureTargetProjectId: '',
  selectedPreviewTilaajavastuuDocument: {},
  projectBankViewMode: 'tree',
  selectedProjectBankFiles: [],
  updateLoading: false,
  updateError: null,
  isDeleting: false,
  selectedProjectBankVisibilityMode: 'PUBLIC',
  selectedProjectBankDataTransferTargetVisibilityMode: 'PUBLIC',
  isAddFolderDialogOpen: false,
  isAddingFolder: false,
  // PRIVATE PROJECT BANK
  selectedPrivateProjectBankFolderId: '',
  privateProjectBankItems: privateProjectBankItemsAdapter.getInitialState(),
  openedPrivateProjectBankTreeFolderIds: [],
  selectedPrivateProjectBankItemIds: [],
};

export const projectBankSlice = createSlice({
  name: 'projectBank',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadProjectFolders.pending, (state, action) => {
        state.projectTreeLoading = true;
      })
      .addCase(loadProjectFolders.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setMany(
          state.projectFolderFilePermissions,
          action.payload
        );
        state.projectTreeLoading = false;
      })
      .addCase(loadProjectFolders.rejected, (state, action) => {
        state.projectTreeLoading = false;
      })

      .addCase(loadProjectBankFolderContents.pending, (state, action) => {
        state.isFolderFilesLoading = true;
      })
      .addCase(loadProjectBankFolderContents.fulfilled, (state, action) => {
        const files = action.payload || [];
        // Apply current sort rule before storing
        const sortBy = state.sortBy || 'date-descending';
        const getDate = (f) =>
          new Date(f?.createdAt || f?.updatedAt || 0).getTime();
        const getName = (f) => (f?.name || '').toLowerCase();
        const getCreator = (f) =>
          (f?.user?.firstName + ' ' + f?.user?.lastName || '')
            .trim()
            .toLowerCase();
        switch (sortBy) {
          case 'name-ascending':
            files.sort((a, b) => getName(a).localeCompare(getName(b)));
            break;
          case 'name-descending':
            files.sort((a, b) => getName(b).localeCompare(getName(a)));
            break;
          case 'date-ascending':
            files.sort((a, b) => getDate(a) - getDate(b));
            break;
          case 'date-descending':
            files.sort((a, b) => getDate(b) - getDate(a));
            break;
          case 'creator-ascending':
            files.sort((a, b) => getCreator(a).localeCompare(getCreator(b)));
            break;
          case 'creator-descending':
            files.sort((a, b) => getCreator(b).localeCompare(getCreator(a)));
            break;
          default:
            files.sort((a, b) => getDate(b) - getDate(a));
        }
        filesFilePermissionsAdapter.setMany(state.filesFilePermissions, files);
        state.isFolderFilesLoading = false;
      })
      .addCase(loadProjectBankFolderContents.rejected, (state, action) => {
        state.isFolderFilesLoading = false;
      })

      .addCase(setSelectedProjectBankFolder, (state, action) => {
        const { folderId, path } = action.payload;
        state.selectedFolderId = folderId;
        state.selectedFolderPath = path;
        state.projectBankViewMode = 'folder';
        if (folderId && !folderId.includes('tilaajavastuu')) {
          state.selectedTilaajavastuuBusinessId = '';
          state.selectedTilaajavastuuFolderProjectId = '';
        }
      })
      .addCase(setSortBy, (state, action) => {
        state.sortBy = action.payload;
      })
      .addCase(
        setAsDeletedProjectBankFilePermission.fulfilled,
        (state, action) => {
          if (action.payload.type === 'FOLDER') {
            projectFolderFilePermissionsAdapter.upsertOne(
              state.projectFolderFilePermissions,
              action.payload
            );
          } else {
            filesFilePermissionsAdapter.upsertOne(
              state.filesFilePermissions,
              action.payload
            );
          }
        }
      )
      .addCase(updateProjectBankItem.pending, (state, action) => {
        state.updateLoading = true;
      })
      .addCase(updateProjectBankItem.fulfilled, (state, action) => {
        if (action.payload.type === 'FOLDER') {
          projectFolderFilePermissionsAdapter.upsertOne(
            state.projectFolderFilePermissions,
            action.payload
          );
        } else {
          filesFilePermissionsAdapter.upsertOne(
            state.filesFilePermissions,
            action.payload
          );
        }
        state.updateLoading = false;
      })
      .addCase(updateProjectBankItem.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.error.message;
      })
      .addCase(createProjectBankFolder.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setOne(
          state.projectFolderFilePermissions,
          action.payload
        );
      })
      .addCase(createProjectBankFile.fulfilled, (state, action) => {
        filesFilePermissionsAdapter.setOne(
          state.filesFilePermissions,
          action.payload
        );
      })
      .addCase(setInitialProjectBankPath, (state, action) => {
        state.initialProjectBankPath = action.payload;
      })
      .addCase(setFolderTree, (state, action) => {
        state.folderTree = action.payload;
      })
      .addCase(closeProjectBank, () => ({
        ...initialState,
      }))
      .addCase(setSelectedTilaajavastuuBusinessId, (state, action) => {
        state.selectedTilaajavastuuBusinessId = action.payload;
      })
      .addCase(openProjectTilaajavastuuFolder, (state, action) => {
        state.selectedTilaajavastuuFolderProjectId = action.payload;
        state.selectedTilaajavastuuBusinessId = '';
      })
      .addCase(openProjectTreeFolders, (state, action) => {
        const openedFolderIds = action.payload;

        const currentOpenProjectTreeFolders = state.openedProjectTreeFolderIds;

        openedFolderIds.map((openedFolderId) => {
          if (!currentOpenProjectTreeFolders.includes(openedFolderId)) {
            state.openedProjectTreeFolderIds = [
              ...currentOpenProjectTreeFolders,
              openedFolderId,
            ];
          }
        });
      })
      .addCase(openPrivateProjectBankTreeFolders, (state, action) => {
        const openedFolderIds = action.payload;

        const currentOpenProjectTreeFolders =
          state.openedPrivateProjectBankTreeFolderIds;

        openedFolderIds.map((openedFolderId) => {
          if (!currentOpenProjectTreeFolders.includes(openedFolderId)) {
            state.openedPrivateProjectBankTreeFolderIds = [
              ...currentOpenProjectTreeFolders,
              openedFolderId,
            ];
          }
        });
      })
      .addCase(closeProjectTreeFolders, (state, action) => {
        const closedFolderIds = action.payload;

        const currentOpenProjectTreeFolders = state.openedProjectTreeFolderIds;

        closedFolderIds.forEach((folderId) => {
          state.openedProjectTreeFolderIds =
            currentOpenProjectTreeFolders.filter(
              (currentOpenFolderId) => currentOpenFolderId !== folderId
            );
        });
      })
      .addCase(closePrivateProjectBankTreeFolders, (state, action) => {
        const closedFolderIds = action.payload;

        const currentOpenProjectTreeFolders =
          state.openedPrivateProjectBankTreeFolderIds;

        closedFolderIds.forEach((folderId) => {
          state.openedPrivateProjectBankTreeFolderIds =
            currentOpenProjectTreeFolders.filter(
              (currentOpenFolderId) => currentOpenFolderId !== folderId
            );
        });
      })
      .addCase(openPdfPreviewDialog, (state, action) => {
        state.selectedPreviewTilaajavastuuDocument = action.payload;
        state.isPdfPreviewDialogOpen = true;
      })
      .addCase(closePdfPreviewDialog, (state, action) => {
        state.isPdfPreviewDialogOpen = false;
        state.selectedPreviewTilaajavastuuDocument = {};
      })
      .addCase(deleteTilaajavastuuDocument.fulfilled, (state, action) => {
        tilaajavastuuDocumentsAdapter.setOne(
          state.tilaajavastuuDocuments,
          action.payload
        );
        state.selectedTilaajavastuuBusinessId = '';
        state.isPdfPreviewDialogOpen = false;
        enqueueSnackbar('Tilaajavastuudokumentti poistettu', {
          variant: 'success',
        });
      })
      .addCase(setProjectBankViewMode, (state, action) => {
        state.projectBankViewMode = action.payload;
      })
      .addCase(toggleSelectProjectBankFile, (state, action) => {
        const alreadySelectedFile = state.selectedProjectBankFiles.includes(
          action.payload
        );

        if (alreadySelectedFile) {
          state.selectedProjectBankFiles =
            state.selectedProjectBankFiles.filter(
              (fileId) => fileId !== action.payload
            );
        } else {
          state.selectedProjectBankFiles.push(action.payload);
        }
      })
      .addCase(clearSelectedProjectBankFiles, (state, action) => {
        state.selectedProjectBankFiles = [];
      })
      .addCase(setIsProjectBankFileSelectDialogOpen, (state, action) => {
        if (action.payload === false) {
          return { ...initialState };
        } else {
          state.isProjectBankFileSelectDialogOpen = action.payload;
        }
      })
      .addCase(openTransferFolderStructureDialog, (state, action) => {
        const { targetProjectId } = action.payload;

        state.isTransferFolderStructureDialogOpen = true;
        state.transferFolderStructureTargetProjectId = targetProjectId;
      })
      .addCase(closeTransferFolderStructureDialog, (state, action) => {
        state.isTransferFolderStructureDialogOpen = false;
        state.transferFolderStructureTargetProjectId = '';
      })
      .addCase(transferFolderStructureToProject.pending, (state, action) => {
        state.isLoadingFolderStructureTransfer = true;
      })
      .addCase(transferFolderStructureToProject.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setMany(
          state.projectFolderFilePermissions,
          action.payload
        );
        state.isTransferFolderStructureDialogOpen = false;
        state.isLoadingFolderStructureTransfer = false;
        enqueueSnackbar('Kansiorakenne tuotu projektille', {
          variant: 'success',
        });
      })
      .addCase(transferFolderStructureToProject.rejected, (state, action) => {
        state.isLoadingFolderStructureTransfer = false;
        enqueueSnackbar('Virhe kansiorakenteen tuonnissa', {
          variant: 'error',
        });
      })
      .addCase(transferItems.pending, (state, action) => {
        state.isLoadingTransfer = true;
      })
      .addCase(transferItems.fulfilled, (state, action) => {
        const response = action.payload;
        const currentFolderId = state.selectedFolderId;
        const currentlyInTargetFolder =
          response.targetFolder && response.targetFolder === currentFolderId;

        for (const item of response.results) {
          if (item.status !== 'SUCCESS') continue;
          if (item.type === 'FOLDER') {
            if (currentlyInTargetFolder) {
              const existing =
                state.projectFolderFilePermissions.entities[item.id];
              projectFolderFilePermissionsAdapter.upsertOne(
                state.projectFolderFilePermissions,
                {
                  ...existing,
                  isBeingTransferred: false,
                  isDeleting: false,
                }
              );
            } else {
              projectFolderFilePermissionsAdapter.removeOne(
                state.projectFolderFilePermissions,
                item.id
              );
            }
          } else {
            if (currentlyInTargetFolder) {
              const existing = state.filesFilePermissions.entities[item.id];
              filesFilePermissionsAdapter.upsertOne(
                state.filesFilePermissions,
                {
                  ...existing,
                  isBeingTransferred: false,
                  isDeleting: false,
                }
              );
            } else {
              filesFilePermissionsAdapter.removeOne(
                state.filesFilePermissions,
                item.id
              );
            }
          }
        }
        state.isLoadingTransfer = false;
      })
      .addCase(transferItems.rejected, (state, action) => {
        state.isLoadingTransfer = false;
      })
      .addCase(duplicateItems.pending, (state, action) => {
        state.isLoadingDuplicate = true;
      })
      .addCase(duplicateItems.fulfilled, (state, action) => {
        const response = action.payload;
        // console.log('response:', response)
        state.isLoadingDuplicate = false;
      })
      .addCase(duplicateItems.rejected, (state, action) => {
        state.isLoadingDuplicate = false;
      })
      .addCase(setUpdateItemsInState, (state, action) => {
        if (!action.payload || !Array.isArray(action.payload)) return;
        for (const item of action.payload) {
          if (item.type === 'FOLDER') {
            projectFolderFilePermissionsAdapter.upsertOne(
              state.projectFolderFilePermissions,
              item
            );
          } else {
            filesFilePermissionsAdapter.upsertOne(
              state.filesFilePermissions,
              item
            );
          }
        }
      })
      .addCase(
        loadBusinessTilaajavastuuIntegrationStatus.fulfilled,
        (state, action) => {
          const { businessId, status } = action.payload;

          state.tilaajavastuuIntegrationStatusesByBusinessId[businessId] =
            status;
        }
      )
      .addCase(setSelectedPrivateProjectBankFolderId, (state, action) => {
        state.selectedPrivateProjectBankFolderId = action.payload;
        state.selectedPrivateProjectBankItemIds = [];
      })
      .addCase(
        loadPrivateProjectBankItemsOfProject.fulfilled,
        (state, action) => {
          privateProjectBankItemsAdapter.setMany(
            state.privateProjectBankItems,
            action.payload
          );
        }
      )
      .addCase(setSelectedProjectBankVisibilityMode, (state, action) => {
        state.selectedProjectBankVisibilityMode = action.payload;
      })

      .addCase(
        setSelectedProjectBankDataTransferTargetVisibilityMode,
        (state, action) => {
          state.selectedProjectBankDataTransferTargetVisibilityMode =
            action.payload;
        }
      )
      .addCase(uploadPrivateProjectBankFile.fulfilled, (state, action) => {
        const { createdFileItems } = action.payload;

        if (createdFileItems && createdFileItems.length > 0) {
          privateProjectBankItemsAdapter.addMany(
            state.privateProjectBankItems,
            createdFileItems
          );
        }
      })
      .addCase(createPrivateProjectBankFolder.pending, (state, action) => {
        state.isAddingFolder = true;
      })
      .addCase(createPrivateProjectBankFolder.fulfilled, (state, action) => {
        const { createdFolder } = action.payload;
        if (createdFolder) {
          privateProjectBankItemsAdapter.addOne(
            state.privateProjectBankItems,
            createdFolder
          );
        }
        state.isAddFolderDialogOpen = false;
        state.isAddingFolder = false;
        enqueueSnackbar(i18n.t('bank.add_folder_success'), {
          variant: 'success',
        });
      })
      .addCase(createPrivateProjectBankFolder.rejected, (state, action) => {
        state.isAddingFolder = false;
        enqueueSnackbar(i18n.t('bank.add_folder_error'), {
          variant: 'error',
        });
      })
      .addCase(setIsAddFolderDialogOpen, (state, action) => {
        state.isAddFolderDialogOpen = action.payload;
      })
      .addCase(deletePrivateProjectBankItem.fulfilled, (state, action) => {
        const { deletedPrivateProjectBankItems } = action.payload;

        privateProjectBankItemsAdapter.upsertMany(
          state.privateProjectBankItems,
          deletedPrivateProjectBankItems
        );
      })
      .addCase(renamePrivateProjectBankItem.pending, (state, action) => {
        state.updateLoading = true;
      })
      .addCase(renamePrivateProjectBankItem.fulfilled, (state, action) => {
        privateProjectBankItemsAdapter.upsertOne(
          state.privateProjectBankItems,
          action.payload
        );
        state.updateLoading = false;
        enqueueSnackbar(i18n.t('common.success'), { variant: 'success' });
      })
      .addCase(renamePrivateProjectBankItem.rejected, (state, action) => {
        state.updateLoading = false;
        enqueueSnackbar(i18n.t('common.error'), { variant: 'error' });
      })
      .addCase(selectPrivateProjectBankItem, (state, action) => {
        const currentSelectedItemIds = state.selectedPrivateProjectBankItemIds;

        if (currentSelectedItemIds.includes(action.payload)) {
          state.selectedPrivateProjectBankItemIds =
            currentSelectedItemIds.filter(
              (itemId) => itemId !== action.payload
            );
        } else {
          state.selectedPrivateProjectBankItemIds = [
            ...currentSelectedItemIds,
            action.payload,
          ];
        }
      })
      .addCase(clearSelectedPrivateProjectBankItems, (state, action) => {
        state.selectedPrivateProjectBankItemIds = [];
      })

      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.pending,
        (state, action) => {
          const transferMethod = action.meta.arg.transferMethod;

          const notificationText =
            transferMethod === 'COPY'
              ? i18n.t('bank.duplicating')
              : i18n.t('bank.moving');

          enqueueSnackbar(notificationText, {
            variant: 'info',
            persist: true,
            key: 'transfer-items-loading',
          });
        }
      )
      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.fulfilled,
        (state, action) => {
          const {
            deletedPrivateProjectBankItems,
            createdPrivateProjectBankItems,
            deletedPublicProjectBankFilePermissions,
            createdPublicProjectBankFilePermissions,
          } = action.payload;

          if (
            deletedPrivateProjectBankItems &&
            deletedPrivateProjectBankItems.length > 0
          ) {
            const deletedIds = deletedPrivateProjectBankItems.map(
              (item) => item.id
            );
            privateProjectBankItemsAdapter.removeMany(
              state.privateProjectBankItems,
              deletedIds
            );
          }

          if (
            createdPrivateProjectBankItems &&
            createdPrivateProjectBankItems.length > 0
          ) {
            privateProjectBankItemsAdapter.upsertMany(
              state.privateProjectBankItems,
              createdPrivateProjectBankItems
            );
          }

          if (
            deletedPublicProjectBankFilePermissions &&
            deletedPublicProjectBankFilePermissions.length > 0
          ) {
            const deletedIds = deletedPublicProjectBankFilePermissions.map(
              (item) => item.id
            );

            filesFilePermissionsAdapter.removeMany(
              state.filesFilePermissions,
              deletedIds
            );
            projectFolderFilePermissionsAdapter.removeMany(
              state.projectFolderFilePermissions,
              deletedIds
            );
          }
          if (
            createdPublicProjectBankFilePermissions &&
            createdPublicProjectBankFilePermissions.length > 0
          ) {
            const files = createdPublicProjectBankFilePermissions.filter(
              (item) => item.type === 'FILE'
            );

            const folders = createdPublicProjectBankFilePermissions.filter(
              (item) => item.type === 'FOLDER'
            );
            filesFilePermissionsAdapter.upsertMany(
              state.filesFilePermissions,
              files
            );
            projectFolderFilePermissionsAdapter.upsertMany(
              state.projectFolderFilePermissions,
              folders
            );
          }

          closeSnackbar('transfer-items-loading');
          enqueueSnackbar(i18n.t('common.success'), {
            variant: 'success',
          });
        }
      )
      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.rejected,
        (state, action) => {
          closeSnackbar('transfer-items-loading');
          enqueueSnackbar(i18n.t('common.error'), {
            variant: 'error',
          });
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.pending,
          loadProjectAndSubProjectTilaajavastuuDocuments.pending
        ),
        (state, action) => {
          state.isLoadingTilaajavastuuDocuments = true;
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.fulfilled,
          loadProjectAndSubProjectTilaajavastuuDocuments.fulfilled
        ),
        (state, action) => {
          tilaajavastuuDocumentsAdapter.setMany(
            state.tilaajavastuuDocuments,
            action.payload
          );
          state.isLoadingTilaajavastuuDocuments = false;
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.rejected,
          loadProjectAndSubProjectTilaajavastuuDocuments.rejected
        ),
        (state, action) => {
          state.isLoadingTilaajavastuuDocuments = false;
        }
      );
  },
});
